import { currencySymbol } from "../../helper/commonHelper";
import { ACTION_ICONS, Admin_Code } from "../../config/siteConfig";
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import moment from 'moment';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap';
import { useLoadingBar } from 'react-top-loading-bar';
import { CSVLink } from 'react-csv';
import ContentLoader from '../loader/ContentLoader';
import DetailView from '../location/DetailView';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { supplierWalletService } from '../../services/supplier-wallet.service';
import { suppliersService } from '../../services/suppliers.service';
import { agentsService } from '../../services/agents.service';
import DatePicker from 'react-multi-date-picker';
import { formatDateYYYYMMDD } from '../../utils/dateUtils';
import { authStorageService } from '../../services/auth-storage.service';

const selectStyles = {
  control: (base, state) => ({
    ...base, borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", minHeight: 34, height: 34,
    fontSize: 13, boxShadow: "none", "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  option: (base, state) => ({
    ...base, fontSize: 13,
    background: state.isSelected ? "var(--btn-bg)" : state.isFocused ? "#e6f1f8" : "#fff",
    color: state.isSelected ? "#fff" : "#2d3748"
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const initialFilters = {
  search: "", supplier_code: "", supplier_label: "", agent_code: "", agent_label: "",
  wallet_owner: "", transaction_type: "", wallet_transaction_type: "", status: "", from: "", to: "",
};

const inputStyle = { borderRadius: 8, border: "1px solid #e8edf5", backgroundColor: "#f8f9fe", height: 34, fontSize: 13 };

const num = (v) => Number(v) || 0;
const money = (v, currency = "INR") =>
  `${currency == "INR" ? "₹" : `${currency} `}${num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const labelOf = (v) => (v ? String(v).replace(/_/g, " ").toUpperCase() : "-");
const formatDate = (v) => (v ? moment(v).format("DD MMM YYYY") : "-");
const formatDateTime = (v) => (v ? moment(v).format("DD MMM YYYY, hh:mm A") : "-");

const STATUS_STYLES = {
  success: { bg: "#d4edda", color: "#155724" },
  pending: { bg: "#fff3cd", color: "#856404" },
  failed: { bg: "#f8d7da", color: "#721c24" },
  reversed: { bg: "#e7f5ff", color: "#1971c2" },
};

const SupplierLedger = () => {
  const { start, complete } = useLoadingBar();
  const isAdmin = Number(authStorageService.getUser()?.role_code) === Admin_Code;

  const [loading, setLoading] = useState(false);
  const [ledgerRes, setLedgerRes] = useState({ pagination: {}, transactions: [] });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [goPage, setGoPage] = useState("");

  const [viewTxn, setViewTxn] = useState(null);

  useEffect(() => {
    loadTransactions();
  }, [pagingLimit, pageValue, appliedFilters]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadTransactions = async () => {
    try {
      setLoading(true);
      start();
      const res = await supplierWalletService.transactions({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        search: appliedFilters.search,
        wallet_owner: appliedFilters.wallet_owner || undefined,
        // agent_code / supplier_code filters are admin only
        ...(isAdmin ? { agent_code: appliedFilters.agent_code, supplier_code: appliedFilters.supplier_code } : {}),
        transaction_type: appliedFilters.transaction_type || undefined,
        wallet_transaction_type: appliedFilters.wallet_transaction_type || undefined,
        status: appliedFilters.status || undefined,
        from: appliedFilters.from,
        to: appliedFilters.to,
      });
      if (res?.success) {
        setLedgerRes({
          pagination: res.resData?.pagination || {},
          transactions: res.resData?.data || []
        })
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false)
      complete();
    }
  }

  // Supplier dropdown: suppliers > list (status = active), searched on the server; value is the supplier_code the ledger API filters on
  const loadSuppliers = async (search = "") => {
    try {
      const res = await suppliersService.list({ page: 1, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return (res.resData?.data || []).map(s => ({ value: s.supplier_code, label: `${s.agency_name} (${s.supplier_code})` }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  // Agent dropdown (admin only): agents > list, searched on the server; value is the agent_code the ledger API filters on
  const loadAgents = async (search = "") => {
    try {
      const res = await agentsService.list({ page: 1, ...(search ? { search } : {}) });
      if (res?.success) {
        return (res.resData?.data || []).map(a => ({ value: a.agent_code, label: `${a.company_name} (${a.agent_code})` }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  const setFilter = (patch) => setFilters(prev => ({ ...prev, ...patch }));

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({
      ...filters,
      from: filters.from ? formatDateYYYYMMDD(filters.from) : "",
      to: filters.to ? formatDateYYYYMMDD(filters.to) : "",
    });
  }

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  }

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  }

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  }

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  }

  const totalRecords = ledgerRes?.pagination?.total || 0;
  const totalPage = ledgerRes?.pagination?.totalPages || 1;
  const transactions = ledgerRes?.transactions || [];
  const pageCredits = transactions.filter(t => t?.transaction_type == "credit").reduce((sum, t) => sum + num(t.amount), 0);
  const pageDebits = transactions.filter(t => t?.transaction_type == "debit").reduce((sum, t) => sum + num(t.amount), 0);
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  }

  const csvData = transactions.map(t => ({
    transaction_ref: t.transaction_ref,
    supplier_name: t.supplier?.agency_name || "",
    supplier_code: t.supplier?.supplier_code || "",
    agent_name: t.agent?.company_name || "",
    agent_code: t.agent?.agent_code || "",
    wallet_transaction_type: labelOf(t.wallet_transaction_type),
    debit: t.transaction_type == "debit" ? num(t.amount).toFixed(2) : "",
    credit: t.transaction_type == "credit" ? num(t.amount).toFixed(2) : "",
    balance_after: num(t.balance_after).toFixed(2),
    currency_code: t.currency_code,
    payment_mode: t.payment_mode || "",
    payment_reference: t.payment_reference || "",
    booking_id: t.booking_id ?? "",
    inventory_id: t.inventory_id ?? "",
    status: t.status,
    date: formatDateTime(t.created_at),
    remark: t.remark || "",
  }));

  const csvHeaders = [
    { label: "Transaction Ref", key: "transaction_ref" },
    ...(isAdmin ? [
      { label: "Supplier Name", key: "supplier_name" },
      { label: "Supplier Code", key: "supplier_code" },
      { label: "Agent Name", key: "agent_name" },
      { label: "Agent Code", key: "agent_code" },
    ] : []),
    { label: "Type", key: "wallet_transaction_type" },
    { label: "Debit", key: "debit" },
    { label: "Credit", key: "credit" },
    { label: "Balance", key: "balance_after" },
    { label: "Currency", key: "currency_code" },
    { label: "Payment Mode", key: "payment_mode" },
    { label: "Payment Reference", key: "payment_reference" },
    { label: "Booking ID", key: "booking_id" },
    { label: "Inventory ID", key: "inventory_id" },
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
    { label: "Remark", key: "remark" },
  ];

  // the Supplier column (supplier / agent details) is shown to admin only
  const tableHeaders = [
    "S.No.", "Transaction", ...(isAdmin ? ["Supplier/Agent"] : []), "Type", "Debit", "Credit", "Balance",
    "Payment", "Status", "Date", "Remark", "Actions",
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    borderRadius: 10, background: bg, border: `1px solid ${border}`, color, fontSize: 13
  });

  const thStyle = {
    fontSize: "11px", fontWeight: "700", color: "#475569",
    textTransform: "uppercase", letterSpacing: "0.6px",
    borderBottom: "2px solid #e8edf5", padding: "10px 12px",
    whiteSpace: "nowrap", background: "#f8f9fe"
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Supplier Ledger</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/suppliers/list" style={{ color: "#475569" }}>Suppliers</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Supplier Ledger</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename="supplier-ledger.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-6 col-lg-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={inputStyle}
                  placeholder="Search transaction / payment ref"
                  value={filters.search}
                  onChange={(e) => setFilter({ search: e.target.value })}
                />
              </div>
              {isAdmin && (
                <>
                  <div className="col-12 col-md-6 col-lg-3">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadSuppliers}
                      value={filters.supplier_code ? { value: filters.supplier_code, label: filters.supplier_label || filters.supplier_code } : null}
                      onChange={(o) => setFilter({ supplier_code: o ? o.value : "", supplier_label: o ? o.label : "" })}
                      placeholder="Select Supplier"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAgents}
                      value={filters.agent_code ? { value: filters.agent_code, label: filters.agent_label || filters.agent_code } : null}
                      onChange={(o) => setFilter({ agent_code: o ? o.value : "", agent_label: o ? o.label : "" })}
                      placeholder="Select Agent"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="col-6 col-md-3 col-lg-2">
                <select className="form-select form-select-sm" style={inputStyle} value={filters.wallet_owner} onChange={(e) => setFilter({ wallet_owner: e.target.value })}>
                  <option value="">All Wallets</option>
                  <option value="supplier">Supplier</option>
                  <option value="agent">Agent</option>
                </select>
              </div>
                </>
              )}              
              <div className="col-6 col-md-3 col-lg-2">
                <select className="form-select form-select-sm" style={inputStyle} value={filters.transaction_type} onChange={(e) => setFilter({ transaction_type: e.target.value })}>
                  <option value="">All Directions</option>
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                </select>
              </div>
              <div className="col-6 col-md-3 col-lg-2">
                <select className="form-select form-select-sm" style={inputStyle} value={filters.wallet_transaction_type} onChange={(e) => setFilter({ wallet_transaction_type: e.target.value })}>
                  <option value="">All Types</option>
                  <option value="topup">Topup</option>
                  <option value="booking_debit">Booking Debit</option>
                  <option value="booking_credit">Booking Credit</option>
                </select>
              </div>
              <div className="col-6 col-md-3 col-lg-2">
                <select className="form-select form-select-sm" style={inputStyle} value={filters.status} onChange={(e) => setFilter({ status: e.target.value })}>
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="col-6 col-md-3 col-lg-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.from || null}
                  onChange={(d) => setFilter({ from: d ? d.format("DD-MM-YYYY") : "" })}
                  format="DD-MM-YYYY"
                  placeholder="From date"
                  maxDate={filters.to || undefined}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...inputStyle, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-3 col-lg-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.to || null}
                  onChange={(d) => setFilter({ to: d ? d.format("DD-MM-YYYY") : "" })}
                  format="DD-MM-YYYY"
                  placeholder="To date"
                  minDate={filters.from || undefined}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...inputStyle, width: "100%" }}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total Transactions</span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{money(pageCredits)}</b> Credits on this page</span>
          <span style={chip("#c92a2a", "#fff5f5", "#ffc9c9")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{money(pageDebits)}</b> Debits on this page</span>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div>
            <div style={{ position: 'relative' }}>
              {loading && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(255,255,255,0.6)', display: 'flex',
                  justifyContent: 'center', alignItems: 'center', zIndex: 10
                }}>
                  <ContentLoader loaderType="circleSpin" />
                </div>
              )}
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr style={{ background: "#f8f9fe" }}>
                      {tableHeaders.map((h, i) => (
                        <th key={i} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length == 0 && !loading && (
                      <tr>
                        <td colSpan={tableHeaders.length} className="text-center py-5" style={{ color: "#475569", fontSize: 13 }}>
                          <i className="bi bi-journal-x d-block mb-1" style={{ fontSize: 28 }}></i>
                          No transactions found
                        </td>
                      </tr>
                    )}
                    {transactions.map((txn, index) => {
                      const isCredit = txn?.transaction_type == "credit";
                      const st = STATUS_STYLES[String(txn?.status).toLowerCase()] || { bg: "#f1f3f4", color: "#444" };
                      return (
                        <tr
                          key={txn?.id}
                          style={{ borderBottom: '1px solid #f0f2f5', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8f9ff'}
                          onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'}
                        >
                      <td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + index + 1}</td>
                          <td className="ps-3">
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 34, height: 34, background: isCredit ? '#e6f7f1' : '#fce8e6', flexShrink: 0 }}
                              >
                                <i className={`bi ${isCredit ? "bi-arrow-down-left" : "bi-arrow-up-right"}`} style={{ fontSize: 14, color: isCredit ? '#0f8a5f' : '#d93025' }}></i>
                              </div>
                              <div>
                                <div className="fw-semibold text-dark" style={{ fontSize: 13, fontFamily: "monospace" }}>{txn?.transaction_ref}</div>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                  {txn?.booking_id ? `Booking #${txn.booking_id}` : `Txn #${txn?.id}`}
                                </div>
                              </div>
                            </div>
                          </td>
                          {isAdmin && (
                            <td>
                              <div className="fw-medium" style={{ fontSize: 13 }}>{txn?.supplier?.agency_name || txn?.agent?.company_name}</div>
                              {txn?.supplier && <div style={{ fontSize: 10, color: "#0f8a64" }}>Supplier: {txn?.supplier?.supplier_code}</div>}
                              {txn?.agent && <div style={{ fontSize: 10, color: "#0e6a96" }}>Agent: {txn.agent.agent_code}</div>}
                            </td>
                          )}
                          <td>
                            <span className="badge rounded-pill" style={{ background: isCredit ? '#e6f7f1' : '#fce8e6', color: isCredit ? '#0f8a5f' : '#d93025', fontWeight: 600, fontSize: 11 }}>
                              {labelOf(txn?.wallet_transaction_type)}
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap", fontSize: 13, fontWeight: 600, color: "#d93025" }}>
                            {isCredit ? "-" : money(txn?.amount, txn?.currency_code)}
                          </td>
                          <td style={{ whiteSpace: "nowrap", fontSize: 13, fontWeight: 600, color: "#0f8a5f" }}>
                            {isCredit ? money(txn?.amount, txn?.currency_code) : "-"}
                          </td>
                          <td style={{ whiteSpace: "nowrap", fontSize: 13, fontWeight: 700, color: "var(--btn-bg)" }}>
                            {money(txn?.balance_after, txn?.currency_code)}
                          </td>
                          <td>
                            <div className="text-capitalize" style={{ fontSize: 13 }}>{txn?.payment_mode || "-"}</div>
                            {txn?.payment_reference && <div className="text-muted" style={{ fontSize: 11 }}>{txn.payment_reference}</div>}
                          </td>
                          <td>
                            <span className="badge" style={{ background: st.bg, color: st.color, fontWeight: 600, fontSize: 11, borderRadius: 8, padding: "5px 10px" }}>
                              {labelOf(txn?.status)}
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: 13 }}>{formatDate(txn?.created_at)}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{txn?.created_at ? moment(txn.created_at).format("hh:mm A") : ""}</div>
                          </td>
                          <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: "#4b5563" }} title={txn?.remark || ""}>
                            {txn?.remark || "-"}
                          </td>
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <button
                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                onClick={() => setViewTxn(txn)}
                                title="View"
                                style={{ width: 32, height: 32, borderRadius: 8, background: '#e6f7f1', color: '#0f8a5f', border: 'none', padding: 0 }}
                              >
                                <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
              <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> transactions</div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span>Rows</span>
                <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                  {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
                </select>
                <span>Go</span>
                <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                  value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
                <span>/ {totalPage}</span>
                <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
                <div className="d-flex align-items-center gap-1">
                  <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                  <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                  {getPageNumbers().map((n, i) => n === "..."
                    ? <span key={"e" + i} className="px-1">...</span>
                    : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                  <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                  <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal size="lg" show={!!viewTxn} onHide={() => setViewTxn(null)}>
          <Modal.Header closeButton closeVariant="white" style={{ background: 'linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)', borderBottom: 'none' }}>
            <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Transaction Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '24px', background: '#f8f9fe' }}>
            {viewTxn && (
              <DetailView fields={[
                { label: "Transaction Ref", value: viewTxn.transaction_ref },
                { label: "Transaction ID", value: viewTxn.id },
                { label: "Supplier", value: viewTxn.supplier ? `${viewTxn.supplier.agency_name} (${viewTxn.supplier.supplier_code})` : null },
                { label: "Transaction Type", value: labelOf(viewTxn.transaction_type) },
                { label: "Wallet Transaction Type", value: labelOf(viewTxn.wallet_transaction_type) },
                { label: "Amount", value: money(viewTxn.amount, viewTxn.currency_code) },
                { label: "Balance After", value: money(viewTxn.balance_after, viewTxn.currency_code) },
                { label: "Currency", value: currencySymbol(viewTxn.currency_code) },
                { label: "Payment Mode", value: viewTxn.payment_mode ? labelOf(viewTxn.payment_mode) : null },
                { label: "Payment Reference", value: viewTxn.payment_reference },
                { label: "Booking ID", value: viewTxn.booking_id },
                { label: "Inventory ID", value: viewTxn.inventory_id },
                { label: "Agent", value: viewTxn.agent ? `${viewTxn.agent.company_name} (${viewTxn.agent.agent_code})` : null },
                { label: "Status", value: labelOf(viewTxn.status), badge: true },
                { label: "Created At", value: formatDateTime(viewTxn.created_at) },
                { label: "Modified At", value: formatDateTime(viewTxn.modified_at) },
                { label: "Remark", value: viewTxn.remark, full: true },
              ]} />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  )
}

export default SupplierLedger
